import React from "react";
import ReactGA from "react-ga";
import {
  Modal,
  ModalBody,
  Button,
  FormGroup,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
} from "reactstrap";
import { conversions, reportConversion } from "utils/googleAds";
import "./ExitIntentPopup.scss";

const SESSION_SHOWN_KEY = "exit_intent_shown";
const LEAD_CAPTURED_KEY = "rv_lead_captured";
const ARM_DELAY_MS = 4000;
const EMAIL_PATTERN =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

class ExitIntentPopup extends React.Component {
  state = {
    visible: false,
    name: "",
    email: "",
    phone: "",
    submitting: false,
    resultMessage: "",
    resultColor: "red",
    submitted: false,
  };

  armed = false;
  isMobile = window.innerWidth <= 768;

  componentDidMount() {
    // Exit intent relies on mouse movement, so it only makes sense on
    // pointer/desktop devices, and only for visitors we haven't already
    // shown it to or captured a lead from.
    if (
      this.isMobile ||
      window.sessionStorage.getItem(SESSION_SHOWN_KEY) ||
      window.localStorage.getItem(LEAD_CAPTURED_KEY)
    ) {
      return;
    }

    this.armTimeout = window.setTimeout(() => {
      this.armed = true;
    }, ARM_DELAY_MS);

    document.addEventListener("mouseout", this.handleMouseOut);
  }

  componentWillUnmount() {
    document.removeEventListener("mouseout", this.handleMouseOut);
    window.clearTimeout(this.armTimeout);
  }

  handleMouseOut = (event) => {
    // Fires when the cursor leaves the top of the viewport toward the
    // browser chrome (tab bar / back button / closing the tab).
    if (!this.armed || event.clientY > 0 || event.relatedTarget) {
      return;
    }

    this.showPopup();
  };

  showPopup = () => {
    document.removeEventListener("mouseout", this.handleMouseOut);
    window.sessionStorage.setItem(SESSION_SHOWN_KEY, "1");
    this.setState({ visible: true });
    ReactGA.event({
      category: "Client",
      action: "Exit Intent Popup Shown",
    });
  };

  close = () => {
    this.setState({ visible: false });
  };

  handleNameChange = (event) => {
    this.setState({ name: event.target.value });
  };

  handleEmailChange = (event) => {
    this.setState({ email: event.target.value });
  };

  handlePhoneChange = (event) => {
    this.setState({ phone: event.target.value });
  };

  isValid() {
    return (
      this.state.name.trim() !== "" &&
      EMAIL_PATTERN.test(this.state.email.toLowerCase())
    );
  }

  handleSubmit = (event) => {
    event.preventDefault();

    if (!this.isValid() || this.state.submitting) {
      return;
    }

    this.setState({ submitting: true, resultMessage: "" });

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fromName: this.state.name,
        fromEmail: this.state.email,
        fromPhone: this.state.phone,
        message: "Lead captured via exit-intent popup on rvaluefoam.ca.",
      }),
    };

    fetch("/api/send_email", requestOptions)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`);
        }
        return response.json();
      })
      .then((result) => {
        if (result.status === "SUCCESS") {
          window.localStorage.setItem(LEAD_CAPTURED_KEY, "1");
          reportConversion(conversions.formSubmit);
          ReactGA.event({
            category: "Client",
            action: "Submitted Exit Intent Popup",
          });
          this.setState({
            submitting: false,
            submitted: true,
            resultMessage: "Thanks! We'll be in touch shortly.",
            resultColor: "green",
          });
          window.setTimeout(this.close, 2500);
        } else {
          this.setState({
            submitting: false,
            resultMessage:
              "Sorry, something went wrong. Please contact us directly.",
            resultColor: "red",
          });
        }
      })
      .catch(() => {
        this.setState({
          submitting: false,
          resultMessage:
            "Sorry, something went wrong. Please contact us directly.",
          resultColor: "red",
        });
      });
  };

  render() {
    return (
      <Modal
        isOpen={this.state.visible}
        toggle={this.close}
        centered
        className="exit-intent-modal"
      >
        <button
          type="button"
          className="exit-intent-close"
          aria-label="Close"
          onClick={this.close}
        >
          &times;
        </button>
        <ModalBody className="p-lg-5">
          {!this.state.submitted && (
            <>
              <h3 className="text-primary mb-2">Wait, before you go!</h3>
              <p className="text-dark mb-4">
                Let us give you a free spray foam insulation quote. Quick and easy, no commitment required.
              </p>
              <form onSubmit={this.handleSubmit}>
                <FormGroup>
                  <InputGroup className="input-group-alternative">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="ni ni-user-run" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      placeholder="Name"
                      type="text"
                      name="name"
                      value={this.state.name}
                      onChange={this.handleNameChange}
                    />
                  </InputGroup>
                </FormGroup>
                <FormGroup>
                  <InputGroup className="input-group-alternative">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="ni ni-email-83" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      placeholder="Email address"
                      type="email"
                      name="email"
                      value={this.state.email}
                      onChange={this.handleEmailChange}
                    />
                  </InputGroup>
                </FormGroup>
                <FormGroup className="mb-4">
                  <InputGroup className="input-group-alternative">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="ni ni-mobile-button" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      placeholder="Phone number (optional)"
                      type="text"
                      name="phone"
                      value={this.state.phone}
                      onChange={this.handlePhoneChange}
                    />
                  </InputGroup>
                </FormGroup>
                <Button
                  block
                  className="btn-round text-secondary"
                  color="dark"
                  size="lg"
                  type="submit"
                  disabled={!this.isValid() || this.state.submitting}
                >
                  {this.state.submitting ? "Sending..." : "Get My Free Quote"}
                </Button>
              </form>
              {this.state.resultMessage !== "" && (
                <p
                  className="mt-3 mb-0"
                  style={{ color: this.state.resultColor }}
                >
                  {this.state.resultMessage}
                </p>
              )}
            </>
          )}
          {this.state.submitted && (
            <div className="text-center py-4">
              <h3 className="text-primary mb-2">Thank you!</h3>
              <p className="text-dark mb-0">{this.state.resultMessage}</p>
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  }
}

export default ExitIntentPopup;
